import { BlurFilter, Container, Graphics } from 'pixi.js';

/**
 * **目标高亮的屏幕边缘效果。**
 *
 * 行动者在一屏里挑目标（求值 / 抽牌 / 三张同名……）时，指针扫过谁的名字，
 * 谁那一侧就：
 *
 * 1. 屏幕边缘亮起一圈**白色外发光**，轻轻呼吸；
 * 2. 一圈**无填充白色描边的小方块**从边缘飞进来、一边缩小一边消散。
 *
 * 指针移开 → 150ms 淡出；**被选中** → 一直亮到本次行动结束（用户）。
 *
 * ## 为什么把「出现 / 消失」做成 150ms 的透明度补间
 *
 * 边缘光直接开关会啪地闪一下，扫过一整列名字时像频闪。150ms 的补间
 * （FADE_MS）让它读起来是亮起来 / 灭下去，而不是开关灯。
 *
 * ## 随机用 Math.random
 *
 * 纯视效、不需要可复现（和 impactParticles 同一条规矩）：方块往哪儿飞、
 * 多大，对玩家和测试都没有意义。
 */

/** 出现 / 消失的补间时长（毫秒） */
export const TARGET_FADE_MS = 150;
/** 呼吸周期（毫秒） */
const BREATH_MS = 1600;
/**
 * 边缘光带的设计宽度（会乘 UI 倍率）。
 *
 * ⚠️ 用户：「边缘光效果太大了」。所以只有 10 —— 一圈很窄的边，
 *    不是把整个屏幕压暗。粒子尺寸也跟着小了一档。
 */
const EDGE_DESIGN = 10;
/** 同时在飞的方块上限 */
const MAX_PARTICLES = 34;
/** 每毫秒平均生成多少方块 */
const SPAWN_PER_MS = MAX_PARTICLES / 520;
/** 方块寿命范围（毫秒） */
const LIFE_MS: readonly [number, number] = [520, 900];
/** 方块起始边长范围（设计像素） */
const SIZE_DESIGN: readonly [number, number] = [5, 16];
/** 寿命结束时缩到原大的多少 */
const SHRINK_TO = 0.16;
/** 飞入速度范围（设计像素 / 秒） */
const SPEED_DESIGN: readonly [number, number] = [90, 190];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** 起始边长 */
  base: number;
  age: number;
  life: number;
}

export interface TargetEdgeOptions {
  readonly scale: number;
  readonly bounds: { readonly width: number; readonly height: number };
}

export class TargetEdgeEffect {
  /** 加进对局屏 override 层的东西（要盖在背景之上、控件之下） */
  readonly container: Container;

  readonly #glow: Graphics;
  readonly #particles: Graphics;
  readonly #glowFilter: BlurFilter;
  readonly #scale: number;
  #bounds: { width: number; height: number };
  /** 0..1 的出现进度（150ms 补间） */
  #alpha = 0;
  /** 目标状态：1 = 该亮，0 = 该灭 */
  #target = 0;
  #timeMs = 0;
  #spawnAcc = 0;
  #parts: Particle[] = [];
  #rand: () => number = Math.random;

  constructor(options: TargetEdgeOptions) {
    this.#scale = options.scale;
    this.#bounds = { width: options.bounds.width, height: options.bounds.height };

    this.container = new Container();
    this.container.label = 'target-edge';
    this.container.eventMode = 'none';
    this.container.visible = false;

    this.#glow = new Graphics();
    this.#glow.blendMode = 'add';
    this.#glowFilter = new BlurFilter({ strength: EDGE_DESIGN * this.#scale * 0.9, quality: 4 });
    this.#glow.filters = [this.#glowFilter];

    this.#particles = new Graphics();

    this.container.addChild(this.#glow, this.#particles);
    this.#paintGlow();
  }

  /** 现在该不该亮（行动者 hover 或选中了某个目标） */
  setActive(on: boolean): void {
    this.#target = on ? 1 : 0;
  }

  get active(): boolean {
    return this.#target > 0;
  }

  /** 出现进度 0..1（诊断 / 测试用） */
  get strength(): number {
    return this.#alpha;
  }

  /** 现在有几颗方块在飞（诊断 / 测试用） */
  get particleCount(): number {
    return this.#parts.length;
  }

  update(deltaMS: number): void {
    const dt = Math.max(0, deltaMS);
    this.#timeMs += dt;
    const step = TARGET_FADE_MS <= 0 ? 1 : dt / TARGET_FADE_MS;
    if (this.#target > this.#alpha) this.#alpha = Math.min(this.#target, this.#alpha + step);
    else if (this.#target < this.#alpha) this.#alpha = Math.max(this.#target, this.#alpha - step);

    if (this.#alpha <= 0.001) {
      if (this.container.visible) {
        this.container.visible = false;
        this.#parts.length = 0;
        this.#particles.clear();
      }
      return;
    }
    this.container.visible = true;

    // 边缘光：轻微呼吸
    const breath = 0.4 + 0.35 * (0.5 + 0.5 * Math.sin((this.#timeMs / BREATH_MS) * Math.PI * 2));
    this.#glow.alpha = this.#alpha * breath;

    if (this.#alpha > 0.05) this.#spawn(dt);
    this.#step(dt);
    this.#draw();
    this.#particles.alpha = this.#alpha;
  }

  resize(bounds: { readonly width: number; readonly height: number }): void {
    this.#bounds = { width: bounds.width, height: bounds.height };
    this.#paintGlow();
  }

  destroy(): void {
    this.#glowFilter.destroy();
    this.container.destroy({ children: true });
  }

  #paintGlow(): void {
    const { width, height } = this.#bounds;
    const edge = EDGE_DESIGN * this.#scale;
    this.#glow
      .clear()
      .rect(0, 0, width, height)
      .stroke({ width: edge, color: 0xffffff, alpha: 1, alignment: 0 });
  }

  #spawn(dt: number): void {
    this.#spawnAcc += SPAWN_PER_MS * dt;
    while (this.#spawnAcc >= 1 && this.#parts.length < MAX_PARTICLES) {
      this.#spawnAcc -= 1;
      this.#parts.push(this.#make());
    }
    if (this.#parts.length >= MAX_PARTICLES) this.#spawnAcc = 0;
  }

  #make(): Particle {
    const { width, height } = this.#bounds;
    const margin = 40 * this.#scale;
    const edge = Math.floor(this.#rand() * 4);
    let x: number;
    let y: number;
    if (edge === 0) {
      x = this.#rand() * width;
      y = -margin;
    } else if (edge === 1) {
      x = width + margin;
      y = this.#rand() * height;
    } else if (edge === 2) {
      x = this.#rand() * width;
      y = height + margin;
    } else {
      x = -margin;
      y = this.#rand() * height;
    }
    const toCenter = Math.atan2(height / 2 - y, width / 2 - x) + (this.#rand() - 0.5) * 0.5;
    const speed = this.#lerp(SPEED_DESIGN) * this.#scale;
    return {
      x,
      y,
      vx: Math.cos(toCenter) * speed,
      vy: Math.sin(toCenter) * speed,
      base: this.#lerp(SIZE_DESIGN) * this.#scale,
      age: 0,
      life: this.#lerp(LIFE_MS),
    };
  }

  #step(dt: number): void {
    const seconds = dt / 1000;
    const alive: Particle[] = [];
    for (const p of this.#parts) {
      p.age += dt;
      if (p.age >= p.life) continue;
      p.x += p.vx * seconds;
      p.y += p.vy * seconds;
      alive.push(p);
    }
    this.#parts = alive;
  }

  #draw(): void {
    this.#particles.clear();
    const stroke = Math.max(1, 1.4 * this.#scale);
    for (const p of this.#parts) {
      const u = p.age / p.life;
      const size = p.base * (1 - u * (1 - SHRINK_TO));
      const alpha = 1 - u;
      this.#particles
        .rect(p.x - size / 2, p.y - size / 2, size, size)
        .stroke({ width: stroke, color: 0xffffff, alpha });
    }
  }

  #lerp(range: readonly [number, number]): number {
    return range[0] + this.#rand() * (range[1] - range[0]);
  }
}
